import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Users, FileText, Calendar, Globe, Shield, Clock, Phone, Briefcase, GraduationCap, Heart, Plane, Building, Flag, MapPin, Search, Target, Send, Plus, AlertCircle, DollarSign, TrendingUp, Award, HelpCircle, Scale, X, CreditCard } from 'lucide-react';

const GermanyEU = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    transition: { duration: 0.6 },
    viewport: { once: true }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <motion.section 
        className="bg-custom-pattern py-16 px-6 text-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-6xl font-inter font-bold md:font-black text-acBlack leading-tight mb-6">
            Germany / EU
          </h1>
          <p className="text-lg text-gray-600 mb-8 max-w-3xl mx-auto">
            Practical guidance for work, study, family, visits, permanent residence, and citizenship across Germany and key EU states.
          </p>
          
          <div className="bg-white rounded-lg shadow-lg p-8 text-left max-w-3xl mx-auto">
            <h2 className="text-2xl font-semibold text-acBlack mb-4">Snapshot</h2>
            <p className="text-gray-600 leading-relaxed">
              We help individuals and families plan, document, and file for Germany and other EU countries. We cover EU Blue Card and national work permits, intra-company transfers, posted-worker notifications, Schengen business travel, study and family routes, permanent residence, and citizenship. Response time is 24 to 72 hours. London covers EMEA. New York runs an APAC desk with evening ET overlap.
            </p>
          </div>
        </div>
      </motion.section>

      {/* Who this is for Section */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            Who this is for
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                <Briefcase className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Professionals</h3>
              <p className="text-gray-600 text-sm">Professionals with a job offer in Germany or in another EU member state</p>
            </div>
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Intra-Company Transferees</h3>
              <p className="text-gray-600 text-sm">Intra-company transferees, short assignments, or project staff who must register posted-worker notifications</p>
            </div>
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                <GraduationCap className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Students</h3>
              <p className="text-gray-600 text-sm">Students who need a national study visa and post-study work planning</p>
            </div>
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Family Members</h3>
              <p className="text-gray-600 text-sm">Spouses, partners, and children joining residents</p>
            </div>
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                <Plane className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Business Visitors</h3>
              <p className="text-gray-600 text-sm">Short-stay business visitors and tourists within the Schengen area</p>
            </div>
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                <Flag className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Residents</h3>
              <p className="text-gray-600 text-sm">Residents planning permanent residence or citizenship</p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* What we handle at a glance Section */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            What we handle at a glance
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-inter font-bold text-acBlack mb-4">Employment & Business</h3>
              <p className="text-gray-600 text-sm mb-4">EU Blue Card where salary and qualification rules are met, national work permits, Intra-Corporate Transferee permits, local permits for shortage occupations, posted-worker notifications, and Schengen business travel planning</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-inter font-bold text-acBlack mb-4">Students & Graduates</h3>
              <p className="text-gray-600 text-sm mb-4">National study visas and residence permits, blocked account planning where required, post-study work options</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-inter font-bold text-acBlack mb-4">Family</h3>
              <p className="text-gray-600 text-sm mb-4">Family reunification for spouses and minor children, dependents of workers and students</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-inter font-bold text-acBlack mb-4">Visitors</h3>
              <p className="text-gray-600 text-sm mb-4">Schengen C visas for business or tourism and visa-free travel planning for eligible nationals</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-inter font-bold text-acBlack mb-4">Permanent Residence</h3>
              <p className="text-gray-600 text-sm mb-4">Long-term residence options such as German Niederlassungserlaubnis and EU long-term residence where criteria are met</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-inter font-bold text-acBlack mb-4">Citizenship</h3>
              <p className="text-gray-600 text-sm mb-4">Naturalisation planning where eligible</p>
            </div>
          </div>
          <p className="text-center text-gray-600 mt-8">
            We do not handle asylum or removal defense.
          </p>
        </div>
      </motion.section>

      {/* Outcomes Section */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            Outcomes you can expect
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Written Plan</h3>
              <p className="text-gray-600 text-sm">Confirms the correct category, fees, timelines, and key risks by country</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Document Pack</h3>
              <p className="text-gray-600 text-sm">Document pack and forms that match rule text and the local authority's format</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Calendar className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Filing Sequence</h3>
              <p className="text-gray-600 text-sm">Filing sequence across consulate, local foreigner's office, and posted-worker platforms with appointments booked</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Interview Preparation</h3>
              <p className="text-gray-600 text-sm">Interview and appointment preparation, including biometric capture and registration steps</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-acBlack" />
              </div>
              <h3 className="text-lg font-inter font-semibold text-acBlack mb-3">Post-Approval Guidance</h3>
              <p className="text-gray-600 text-sm">Post-approval guidance on local registration, health insurance, tax ID, renewals, and long-term planning</p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Employment and Business Routes Section */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            Employment and business routes
          </h2>
          
          <div className="mb-12">
            <h3 className="text-2xl font-inter font-semibold text-acBlack text-center mb-8">Key options</h3>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white p-6 rounded-lg shadow-sm border">
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-3">EU Blue Card</h4>
                <p className="text-gray-600 text-sm mb-3">Germany and other EU states: requires a recognised university degree and a salary that meets the threshold for the occupation or the state. Thresholds change periodically and may be lower for shortage occupations. We confirm the current numbers and recognition rules.</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm border">
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-3">National Work Permits</h4>
                <p className="text-gray-600 text-sm mb-3">For roles that do not fit Blue Card or where vocational recognition applies</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm border">
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-3">Intra-Corporate Transferee (ICT)</h4>
                <p className="text-gray-600 text-sm mb-3">For managers, specialists, and trainees transferred within a group</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm border">
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-3">Posted-Worker Notifications</h4>
                <p className="text-gray-600 text-sm mb-3">Posted-worker notifications and A1 certificates: for short assignments and cross-border services when you remain employed abroad. We confirm when a notification is required, what must be filed, and what documents to carry on site.</p>
              </div>
            </div>
            <div className="mt-6 bg-white p-6 rounded-lg shadow-sm border">
              <h4 className="text-lg font-inter font-semibold text-acBlack mb-3">Business Visitors in Schengen</h4>
              <p className="text-gray-600 text-sm">Meetings and limited activities that do not amount to local employment. We confirm permitted activities and day limits.</p>
            </div>
          </div>

          <div className="bg-blue-50 p-8 rounded-lg">
            <h3 className="text-xl font-inter font-semibold text-acBlack mb-4">What we do</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div>
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-2">Role Mapping</h4>
                <p className="text-gray-600 text-sm">Map the role to the correct permit type and confirm salary and qualification recognition</p>
              </div>
              <div>
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-2">Filing Sequence</h4>
                <p className="text-gray-600 text-sm">Plan the filing sequence: consulate visa, local residence card, and any posted-worker registration</p>
              </div>
              <div>
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-2">Documentation</h4>
                <p className="text-gray-600 text-sm">Coordinate employer letters, contracts, qualification evaluations, and translations</p>
              </div>
              <div>
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-2">Registration Support</h4>
                <p className="text-gray-600 text-sm">Book appointments and guide Anmeldung or local registration after arrival</p>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Students and Graduates Section */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            Students and graduates
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-sm border">
              <h3 className="text-xl font-inter font-semibold text-acBlack mb-6">Study visas and residence permits</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">Course fit review, acceptance letter alignment, and financial proof</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">Blocked account or accepted funding alternatives where required</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">Health insurance options for students and when to switch to statutory or private cover</span>
                </li>
              </ul>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-sm border">
              <h3 className="text-xl font-inter font-semibold text-acBlack mb-6">Post-study</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">Graduate work options and how to convert to a work permit or Blue Card</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">Timing around thesis completion, graduation, and employment start dates</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Family Routes Section */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            Family routes
          </h2>
          
          <div className="mb-12">
            <h3 className="text-2xl font-inter font-semibold text-acBlack text-center mb-8">Common pathways</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-sm border">
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-3">Spouse or Partner</h4>
                <p className="text-gray-600 text-sm">Spouse or partner and minor children joining a resident or worker</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm border">
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-3">Student Dependents</h4>
                <p className="text-gray-600 text-sm">Dependents of students</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm border">
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-3">Blue Card Families</h4>
                <p className="text-gray-600 text-sm">Family of EU Blue Card holders with facilitated access in some states</p>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 p-8 rounded-lg">
            <h3 className="text-xl font-inter font-semibold text-acBlack mb-4">Key elements we handle</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-2">Documentation</h4>
                <p className="text-gray-600 text-sm">Relationship evidence, accommodation space, and health insurance</p>
              </div>
              <div>
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-2">Requirements</h4>
                <p className="text-gray-600 text-sm">Language or integration prerequisites where required by national law</p>
              </div>
              <div>
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-2">Appointments</h4>
                <p className="text-gray-600 text-sm">Appointment scheduling at consulates and local offices</p>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Visitors Section */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            Visitors
          </h2>
          
          <div className="bg-white p-8 rounded-lg shadow-sm border">
            <h3 className="text-xl font-inter font-semibold text-acBlack mb-6 text-center">Schengen short stay</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Calendar className="w-6 h-6 text-acBlack" />
                </div>
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-2">90 in 180 Rule</h4>
                <p className="text-gray-600 text-sm">Business and tourism planning within the 90 in 180 rule</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileText className="w-6 h-6 text-acBlack" />
                </div>
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-2">Documentation</h4>
                <p className="text-gray-600 text-sm">Purpose letters, invitations, conference registrations, and proof of funds</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Globe className="w-6 h-6 text-acBlack" />
                </div>
                <h4 className="text-lg font-inter font-semibold text-acBlack mb-2">Visa Planning</h4>
                <p className="text-gray-600 text-sm">Visa-free nationals get itinerary and compliance guidance. Visa-required nationals get a complete application pack</p>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Permanent Residence and Citizenship Section */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            Permanent residence and citizenship
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-sm">
              <h3 className="text-xl font-inter font-semibold text-acBlack mb-6">Permanent residence</h3>
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-acBlack mb-2">Germany</h4>
                  <p className="text-gray-600 text-sm">Niederlassungserlaubnis and EU long-term residence for eligible residents. We calculate residence days, confirm language and integration requirements, and plan document harvesting such as social insurance and tax proofs.</p>
                </div>
                <div>
                  <h4 className="font-semibold text-acBlack mb-2">Other EU states</h4>
                  <p className="text-gray-600 text-sm">Equivalent long-term residence options and national permanent residence routes</p>
                </div>
              </div>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-sm">
              <h3 className="text-xl font-inter font-semibold text-acBlack mb-6">Citizenship</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">Naturalisation planning with residence, language, integration, and good conduct requirements confirmed for the specific state</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">Dual nationality rules differ by country. We advise the current position for your case</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </motion.section>

      {/* End-to-End Process */}
      <motion.section 
        className="py-16"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-black text-acBlack mb-4 font-inter">End-to-End Process</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our structured approach ensures your Germany/EU immigration application is handled professionally from start to finish.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
            {[
              {
                step: "01",
                title: "Intake and Quick Screen",
                description: "We collect job or study offers, family details, immigration history, and target dates.",
                icon: Search
              },
              {
                step: "02", 
                title: "Strategy Call and Written Plan",
                description: "Category selection, recognition steps for qualifications, salary or funds planning, and the filing sequence across consulate and local offices.",
                icon: Target
              },
              {
                step: "03",
                title: "Evidence and Forms", 
                description: "Document list, templates, translations, and legalisations. We reconcile names, dates, and addresses across records.",
                icon: FileText
              },
              {
                step: "04",
                title: "Submission and Appointments",
                description: "Consular filing, posted-worker notifications if needed, biometrics, and local foreigner's office bookings. Interview coaching where relevant.",
                icon: Send
              },
              {
                step: "05",
                title: "Decision and Next Steps",
                description: "Entry visa issuance, arrival registration, residence card collection, and long-term planning. If refused, we issue a written analysis and advise on refile or review with licensed counsel where required.",
                icon: CheckCircle
              }
            ].map((process, index) => (
              <div key={index} className="text-center">
                <div className="relative mb-6">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <process.icon className="w-8 h-8 text-blue-600" />
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-acBlack text-white rounded-full flex items-center justify-center text-sm font-bold">
                    {process.step}
                  </div>
                </div>
                <h3 className="text-lg font-bold text-acBlack mb-3 font-inter">{process.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Timelines and Service Levels */}
      <motion.section 
        className="py-16 bg-gray-50"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-black text-acBlack mb-4 font-inter">Timelines & Service Levels</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Transparent timelines and service options to match your urgency and requirements.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              {
                title: "Evidence Pack and Forms",
                timeline: "2-6 weeks",
                description: "Depending on recognition steps and translations",
                icon: FileText,
                features: ["Document preparation", "Recognition planning", "Translation coordination", "Quality review"]
              },
              {
                title: "Consular and Local Processing",
                timeline: "Varies",
                description: "By city and season. Appointments can be limited and must be planned early",
                icon: Globe,
                features: ["Consulate appointments", "Local office bookings", "City-dependent timing", "Seasonal variations"],
                highlight: true
              },
              {
                title: "Posted-Worker Notifications",
                timeline: "Fast",
                description: "Often faster but must be filed before work starts on site",
                icon: Clock,
                features: ["Pre-work filing", "A1 certificates", "Notification platforms", "Site documentation"]
              },
              {
                title: "Communication",
                timeline: "24-72 hours",
                description: "Response time, faster during booking and deadline windows",
                icon: Phone,
                features: ["Regular updates", "Priority support", "Deadline management", "Status tracking"]
              }
            ].map((service, index) => (
              <div key={index} className={`bg-white rounded-lg p-6 border ${service.highlight ? 'border-blue-200 bg-blue-50' : 'border-gray-200'} hover:shadow-lg transition-shadow`}>
                <div className="text-center mb-4">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <service.icon className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold text-acBlack mb-2 font-inter">{service.title}</h3>
                  <div className="text-2xl font-black text-blue-600 mb-2">{service.timeline}</div>
                  <p className="text-gray-600 text-sm">{service.description}</p>
                </div>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-sm text-gray-600">
                      <CheckCircle className="w-4 h-4 text-blue-600 mr-2 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Documents Checklist */}
      <motion.section 
        className="py-16"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-black text-acBlack mb-4 font-inter">Documents Checklist by Theme</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Essential documentation requirements organized by category for Germany/EU immigration applications.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                title: "Identity and Immigration",
                description: "Passports, prior visas and permits, travel history, biometric photos that meet country standards",
                icon: Shield
              },
              {
                title: "Employment or Business", 
                description: "Job offer, contract, detailed job description, salary evidence, company registration, assignment or secondment letters, posted-worker notification proof, A1 social security certificate where applicable",
                icon: Briefcase
              },
              {
                title: "Qualifications and Recognition",
                description: "University degree or vocational credentials, transcripts, recognition or equivalence confirmation if required, professional licences where regulated",
                icon: Award
              },
              {
                title: "Students",
                description: "Acceptance letter, proof of funds such as blocked account or sponsor documents, insurance, accommodation plan",
                icon: GraduationCap
              },
              {
                title: "Family",
                description: "Marriage or partnership proof, birth or adoption certificates, accommodation space evidence, health insurance, income where required, language certificates for family routes where national law requires them",
                icon: Heart
              },
              {
                title: "Character and Health",
                description: "Police certificates where required, health insurance proof, and medical tests if the country requests them",
                icon: Shield
              }
            ].map((category, index) => (
              <div key={index} className="bg-white rounded-lg p-6 border border-gray-200 hover:shadow-lg transition-shadow">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                    <category.icon className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-bold text-acBlack font-inter">{category.title}</h3>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">{category.description}</p>
              </div>
            ))}
          </div>

          <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
            <div className="flex items-start">
              <AlertCircle className="w-6 h-6 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-bold text-acBlack mb-2">Country-Specific Requirements</h4>
                <p className="text-gray-700 text-sm">
                  We finalise a tailored list during strategy because formats, thresholds, and recognition rules differ by country and sometimes by state or city.
                </p>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Special Situations */}
      <motion.section 
        className="py-16 bg-gray-50"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-black text-acBlack mb-4 font-inter">Special Situations and How We Handle Them</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Complex cases and unique circumstances that require specialized expertise and strategic approaches.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                title: "Salary Below Threshold for Blue Card",
                description: "We test shortage occupation lists, regional thresholds, or national permit alternatives",
                icon: DollarSign
              },
              {
                title: "Qualification Not Yet Recognised",
                description: "We plan recognition steps or use alternative categories that accept vocational credentials",
                icon: Award
              },
              {
                title: "Tight Start Date with Posted-Worker Rules",
                description: "We file notifications early, confirm documentation to carry on site, and check whether a residence permit is required instead",
                icon: Clock
              },
              {
                title: "Multiple EU Sites or Frequent Travel",
                description: "We plan Schengen stay management and ICT or Blue Card mobility rules where they apply",
                icon: Globe
              },
              {
                title: "Language or Integration Requirements",
                description: "We confirm level and exemptions and schedule tests early for family or PR applications",
                icon: Users
              },
              {
                title: "Previous Refusals",
                description: "We prepare a history summary, address decision points, and rebuild credibility with stronger evidence",
                icon: Shield
              }
            ].map((situation, index) => (
              <div key={index} className="bg-white rounded-lg p-6 border border-gray-200 hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <situation.icon className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-bold text-acBlack mb-3 font-inter">{situation.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{situation.description}</p>
              </div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Fees and What's Included */}
      <motion.section 
        className="py-16"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-black text-acBlack mb-4 font-inter">Fees and What's Included</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Transparent pricing structure with clear inclusions and exclusions for Germany/EU immigration services.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="bg-white rounded-lg p-8 border border-gray-200">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                  <DollarSign className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-2xl font-bold text-acBlack font-inter">Professional Fees</h3>
              </div>
              <ul className="space-y-3 mb-6">
                {[
                  "Strategy call and written plan",
                  "Document preparation and review",
                  "Forms completion and submission",
                  "Appointment booking and coordination",
                  "Communication with authorities",
                  "Status updates and case management"
                ].map((item, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white rounded-lg p-8 border border-gray-200">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mr-4">
                  <X className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-2xl font-bold text-acBlack font-inter">Additional Costs</h3>
              </div>
              <ul className="space-y-3">
                {[
                  "Government and consular fees",
                  "Translation and legalisation costs",
                  "Recognition or equivalence fees",
                  "Biometrics and photo costs",
                  "Travel for appointments",
                  "Courier and shipping fees"
                ].map((item, index) => (
                  <li key={index} className="flex items-start">
                    <X className="w-5 h-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
            <div className="flex items-start">
              <AlertCircle className="w-6 h-6 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-bold text-acBlack mb-2">Fee Structure</h4>
                <p className="text-gray-700 text-sm">
                  We quote a fixed professional fee after the strategy call. Government fees vary by country and category. We provide estimates for all additional costs upfront.
                </p>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Risks and How We Mitigate Them */}
      <motion.section 
        className="py-16 bg-gray-50"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-black text-acBlack mb-4 font-inter">Risks and How We Mitigate Them</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Proactive risk management to maximize your chances of success in Germany/EU immigration applications.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                risk: "Qualification Recognition Delays",
                mitigation: "We start recognition early, use alternative pathways where possible, and maintain contact with recognition bodies",
                icon: Award
              },
              {
                risk: "Appointment Availability",
                mitigation: "We book appointments as soon as eligible, monitor cancellations, and have backup location strategies",
                icon: Calendar
              },
              {
                risk: "Posted-Worker Notification Timing",
                mitigation: "We file notifications well before work starts and ensure all site documentation is ready",
                icon: Clock
              },
              {
                risk: "Salary Threshold Changes",
                mitigation: "We monitor threshold updates and have alternative category strategies prepared",
                icon: TrendingUp
              },
              {
                risk: "Document Translation Issues",
                mitigation: "We use certified translators familiar with immigration requirements and review all translations",
                icon: FileText
              },
              {
                risk: "Country-Specific Rule Changes",
                mitigation: "We track regulatory updates across EU countries and adjust strategies accordingly",
                icon: Shield
              }
            ].map((item, index) => (
              <div key={index} className="bg-white rounded-lg p-6 border border-gray-200 hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
                  <item.icon className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-lg font-bold text-acBlack mb-3 font-inter">{item.risk}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{item.mitigation}</p>
              </div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* After Approval */}
      <motion.section 
        className="py-16"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-black text-acBlack mb-4 font-inter">After Approval: What Happens Next</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Your journey continues after approval with important steps to secure your status and plan for the future.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                step: "Entry and Registration",
                description: "Enter the country within visa validity, register your address with local authorities, and activate your residence status",
                icon: MapPin,
                timeline: "Within 14 days"
              },
              {
                step: "Residence Card Collection",
                description: "Collect your residence card from the local foreigner's office and ensure all details are correct",
                icon: CreditCard,
                timeline: "2-8 weeks"
              },
              {
                step: "Work and Study Commencement",
                description: "Start your employment or studies, ensure compliance with permit conditions, and maintain required documentation",
                icon: Briefcase,
                timeline: "As planned"
              },
              {
                step: "Long-term Planning",
                description: "Plan for renewals, permanent residence eligibility, family reunification, and citizenship pathways",
                icon: Target,
                timeline: "Ongoing"
              }
            ].map((step, index) => (
              <div key={index} className="bg-white rounded-lg p-6 border border-gray-200 hover:shadow-lg transition-shadow text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <step.icon className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-lg font-bold text-acBlack mb-2 font-inter">{step.step}</h3>
                <div className="text-sm font-semibold text-green-600 mb-3">{step.timeline}</div>
                <p className="text-gray-600 text-sm leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* What We Need to Get Started */}
      <motion.section 
        className="py-16 bg-gray-50"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-black text-acBlack mb-4 font-inter">What We Need to Get Started</h2>
            <p className="text-lg text-gray-600">
              Basic information to begin your Germany/EU immigration assessment and strategy development.
            </p>
          </div>

          <div className="bg-white rounded-lg p-8 border border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-bold text-acBlack mb-4 font-inter">Personal Information</h3>
                <ul className="space-y-3">
                  {[
                    "Current nationality and passport details",
                    "Immigration history and current status",
                    "Family composition and their status",
                    "Target country and preferred timeline"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-600 text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-bold text-acBlack mb-4 font-inter">Purpose and Background</h3>
                <ul className="space-y-3">
                  {[
                    "Job offer, study acceptance, or family details",
                    "Qualifications and work experience",
                    "Language abilities and test results",
                    "Any specific concerns or requirements"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-600 text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* FAQs */}
      <motion.section 
        className="py-16"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-black text-acBlack mb-4 font-inter">Frequently Asked Questions</h2>
            <p className="text-lg text-gray-600">
              Common questions about Germany/EU immigration processes and requirements.
            </p>
          </div>

          <div className="space-y-6">
            {[
              {
                question: "Do I need to speak German for all EU countries?",
                answer: "Language requirements vary significantly. Germany requires German for most family and permanent residence applications. Other EU countries have different rules - some accept English, others require the local language. We assess requirements for your specific situation and target country."
              },
              {
                question: "Can I use an EU Blue Card to work in multiple countries?",
                answer: "The EU Blue Card allows mobility after 18 months in the first country, but you need to meet salary thresholds and job requirements in each country. We help plan multi-country strategies and ensure compliance with mobility rules."
              },
              {
                question: "What's the difference between posted-worker notifications and residence permits?",
                answer: "Posted-worker notifications are for temporary assignments (usually under 24 months) where you remain employed by a company outside the host country. Residence permits are for longer-term moves or local employment. The choice affects taxation, social security, and family rights."
              },
              {
                question: "How long does qualification recognition take in Germany?",
                answer: "Recognition timelines vary by profession and state. Academic degrees often take 2-4 months, while regulated professions like medicine or engineering can take 6-12 months. We start recognition early and explore alternative pathways where possible."
              },
              {
                question: "Can my family join me immediately?",
                answer: "Family reunification rules depend on your permit type and the country. EU Blue Card holders and some other categories can bring family immediately. Others may need to wait or meet additional requirements like accommodation and income thresholds."
              },
              {
                question: "What happens if my application is refused?",
                answer: "We provide a detailed analysis of the refusal reasons and advise on options including administrative review, reapplication with additional evidence, or appeal through licensed counsel where available. Many refusals can be overcome with the right strategy."
              }
            ].map((faq, index) => (
              <div key={index} className="bg-white rounded-lg p-6 border border-gray-200 hover:shadow-lg transition-shadow">
                <h3 className="text-lg font-bold text-acBlack mb-3 font-inter">{faq.question}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Compliance and Professional Standards */}
      <motion.section 
        className="py-16 bg-gray-50"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-black text-acBlack mb-4 font-inter">Compliance and Professional Standards</h2>
            <p className="text-lg text-gray-600">
              Our commitment to ethical practice and regulatory compliance across Germany and the EU.
            </p>
          </div>

          <div className="bg-white rounded-lg p-8 border border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-bold text-acBlack mb-4 font-inter">Professional Standards</h3>
                <ul className="space-y-3">
                  {[
                    "Regulated immigration advice where required",
                    "Transparent fee structure and service scope",
                    "Confidential handling of personal information",
                    "Regular training on regulatory changes"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start">
                      <Shield className="w-5 h-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-600 text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-bold text-acBlack mb-4 font-inter">Quality Assurance</h3>
                <ul className="space-y-3">
                  {[
                    "Multi-point review of all applications",
                    "Country-specific expertise and local knowledge",
                    "Continuous monitoring of success rates",
                    "Client feedback and service improvement"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start">
                      <Award className="w-5 h-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-600 text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </motion.section>


    </div>
  );
};

export default GermanyEU;
